import axios from 'axios';
import { getAuthToken } from '../auth/authSession';

const API_BASE = process.env.REACT_APP_API_URL ?? 'http://localhost:5000/api';

interface ApiResponse<T> {
  success: boolean;

  // LOGIN
  token?: string;
  user?: any;

  // LOCUMS
  locums?: T;
  locum?: T;
  data?: T;

  // PHARMACIES
  pharmacies?: T;
  pharmacy?: T;

  // BOOKINGS
  bookings?: T;

  // PAGINATION
  total?: number;
  page?: number;
  totalPages?: number;

  message?: string;
}

const adminClient = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json'
  }
});

// ATTACH TOKEN
adminClient.interceptors.request.use(config => {
  const token = getAuthToken();

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.message ?? error.message;
  }

  return 'Request failed';
};

// ADMIN LOGIN
interface AdminLoginResponse {
  success: boolean;
  token: string;
  user: any;
  message?: string;
}

// ADMIN LOGIN
export const adminLogin = async (payload: {
  email: string;
  password: string;
}) => {
  try {
    const { data } = await adminClient.post<AdminLoginResponse>(
      '/admin/login',
      payload
    );

    if (!data.success) {
      throw new Error(data.message || 'Login failed');
    }

    return data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

// GET ALL LOCUMS
export const getAllLocums = async (filters?: any) => {
  try {
    const { data } = await adminClient.get<ApiResponse<any[]>>(
      '/admin/locums',
      {
        params: filters
      }
    );

    if (!data.success) {
      throw new Error(data.message || 'Failed to fetch locums');
    }

    return data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

// GET SINGLE LOCUM
export const getLocumById = async (id: string | number) => {
  try {
    const { data } = await adminClient.get<ApiResponse<any>>(
      `/admin/locums/${id}`
    );

    if (!data.success) {
      throw new Error(data.message || 'Failed to fetch locum');
    }

    return data.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

// TOGGLE LOCUM STATUS
export const toggleLocumStatus = async (id: string | number) => {
  try {
    const { data } = await adminClient.patch(`/admin/locums/${id}/toggle`);

    if (!data.success) {
      throw new Error(data.message || 'Failed to update status');
    }

    return data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

// GET ALL BOOKINGS FOR A LOCUM
export const getLocumBookings = async (locumId: string | number) => {
  try {
    const { data } = await adminClient.get<ApiResponse<any>>(
      `/admin/locum/${locumId}/bookings`
    );

    if (!data.success) {
      throw new Error(data.message || 'Failed to fetch bookings');
    }

    return data.bookings || [];
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

// GET ALL PHARMACIES
export const getAllPharmacies = async (filters?: any) => {
  try {
    const { data } = await adminClient.get<ApiResponse<any[]>>(
      '/admin/pharmacies',
      {
        params: filters
      }
    );

    if (!data.success) {
      throw new Error(data.message || 'Failed to fetch pharmacies');
    }

    return data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

// GET SINGLE PHARMACY
export const getPharmacyById = async (id: string | number) => {
  try {
    const { data } = await adminClient.get<ApiResponse<any>>(
      `/admin/pharmacies/${id}`
    );

    if (!data.success) {
      throw new Error(data.message || 'Failed to fetch pharmacy');
    }

    return data.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

// TOGGLE PHARMACY STATUS
export const togglePharmacyStatus = async (id: string | number) => {
  try {
    const { data } = await adminClient.patch(`/admin/pharmacies/${id}/toggle`);

    if (!data.success) {
      throw new Error(data.message || 'Failed to update pharmacy status');
    }

    return data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

// APPROVE / REJECT PHARMACY
export const approvePharmacy = async (id: string | number) => {
  try {
    const { data } = await adminClient.patch(`/admin/pharmacies/${id}/approve`);

    if (!data.success) {
      throw new Error(data.message || 'Failed to approve pharmacy');
    }

    return data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const validateLocumCsv = async (
    file: File
) => {

    const formData = new FormData();

    formData.append("file", file);

    const response = await adminClient.post(
        "/admin/locums/validate-csv",
        formData,
        {
            headers: {
                "Content-Type":
                    "multipart/form-data"
            }
        }
    );

    return response.data;
};

export const importLocumCsv = async (records: any[]) => {

  const response = await adminClient.post(
    "/admin/locums/import-csv",
    {
      records
    }
  );

  return response.data;

};

export const validatePharmacyCsv = async (file: File) => {
  const formData = new FormData();
  formData.append("file", file);

  const { data } = await adminClient.post(
    "/admin/pharmacies/validate-csv",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );

  return data;
};

export const importPharmacyCsv = async (records: any[]) => {
  const { data } = await adminClient.post(
    "/admin/pharmacies/import-csv",
    {
      records,
    }
  );

  return data;
};

export const downloadSample = async (
  type: "locum" | "pharmacy"
) => {

  const response = await adminClient.get(
    `/admin/download-sample/${type}`,
    {
      responseType: "blob"
    }
  );

  return response.data;
};

// APPROVE LOCUM
export const approveLocum = async (id: string | number) => {
  try {
    const { data } = await adminClient.patch<ApiResponse<any>>(
      `/admin/locums/${id}/approve`
    );

    if (!data.success) {
      throw new Error(data.message || "Failed to approve locum");
    }

    return data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

// REJECT LOCUM
export const rejectLocum = async (
  id: string | number,
  reason: string
) => {
  try {
    const { data } = await adminClient.patch<ApiResponse<any>>(
      `/admin/locums/${id}/reject`,
      {
        reason
      }
    );

    if (!data.success) {
      throw new Error(data.message || "Failed to reject locum");
    }

    return data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};